import React, { useState, useRef, useEffect } from 'react';

interface VoiceRecorderProps {
  isRecording: boolean;
  isMuted: boolean;
  onMuteToggle: () => void;
  onAudioData: (base64Audio: string) => void;
  connectionId: string | null;
  interviewType: string;
}

const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  isRecording,
  isMuted,
  onMuteToggle,
  onAudioData,
  connectionId: _connectionId,
  interviewType: _interviewType,
}) => {
  const [audioLevel, setAudioLevel] = useState<number>(0);
  const [isListening, setIsListening] = useState<boolean>(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const sendTimerRef = useRef<number | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const lastSendTimeRef = useRef<number>(0);
  const backgroundNoiseLevelRef = useRef<number>(0);
  const adaptiveThresholdRef = useRef<number>(1.0);
  const voiceActivityBufferRef = useRef<boolean[]>([]);
  const isCurrentlySpeakingRef = useRef<boolean>(false);
  const silenceStartTimeRef = useRef<number | null>(null);
  const currentAudioLevelRef = useRef<number>(0);
  const mimeTypeRef = useRef<string>('audio/webm;codecs=opus');
  const isStoppingForSegmentRef = useRef<boolean>(false);
  const isStoppingRef = useRef<boolean>(false);
  const stopTimeoutRef = useRef<number | null>(null);
  const hasSentAudioRef = useRef<boolean>(false);

  useEffect(() => {
    if (isRecording) {
      startRecording();
      setIsListening(true);
    } else {
      stopRecording();
      setIsListening(false);
    }

    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      if (sendTimerRef.current) {
        clearInterval(sendTimerRef.current);
        sendTimerRef.current = null;
      }
      if (
        audioContextRef.current &&
        audioContextRef.current.state !== 'closed'
      ) {
        audioContextRef.current.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRecording]);

  const startRecording = async (): Promise<void> => {
    try {
      console.log('🎤 Requesting microphone access...');
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1,
        },
      });

      const audioTrack = stream.getAudioTracks()[0];
      console.log('🎤 ✅ Microphone access granted:', {
        label: audioTrack.label,
        enabled: audioTrack.enabled,
        muted: audioTrack.muted,
        readyState: audioTrack.readyState,
        settings: audioTrack.getSettings(),
      });

      streamRef.current = stream;

      const AudioContextClass =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        window.AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioContextClass();

      console.log('🎤 AudioContext created:', {
        sampleRate: audioContext.sampleRate,
        state: audioContext.state,
      });

      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);

      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.2;
      analyser.minDecibels = -90;
      analyser.maxDecibels = -10;
      microphone.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      backgroundNoiseLevelRef.current = 0;
      adaptiveThresholdRef.current = 1.0;
      voiceActivityBufferRef.current = [];
      isCurrentlySpeakingRef.current = false;
      silenceStartTimeRef.current = null;

      let calibrationSamples = 0;
      const calibrationDuration = 1000;
      const calibrationStartTime = Date.now();

      const updateLevel = (): void => {
        if (!isRecording || !analyserRef.current || !audioContextRef.current)
          return;

        const analyser = analyserRef.current;
        const frequencyData = new Uint8Array(analyser.frequencyBinCount);
        const timeData = new Uint8Array(analyser.frequencyBinCount);

        analyser.getByteFrequencyData(frequencyData);
        analyser.getByteTimeDomainData(timeData);

        let sum = 0;
        for (let i = 0; i < timeData.length; i++) {
          const normalized = (timeData[i] - 128) / 128;
          sum += normalized * normalized;
        }
        const rms = Math.sqrt(sum / timeData.length);
        const audioLevel = Math.min(rms * 300, 100);
        currentAudioLevelRef.current = audioLevel;
        setAudioLevel(audioLevel);

        const sampleRate = audioContextRef.current.sampleRate;
        const binWidth = sampleRate / analyser.fftSize;
        const voiceStartBin = Math.floor(300 / binWidth);
        const voiceEndBin = Math.floor(3400 / binWidth);

        let voiceEnergy = 0;
        let totalEnergy = 0;
        for (let i = 0; i < frequencyData.length; i++) {
          const energy = frequencyData[i];
          totalEnergy += energy;
          if (i >= voiceStartBin && i <= voiceEndBin) {
            voiceEnergy += energy;
          }
        }

        const voiceRatio = voiceEnergy / (totalEnergy + 1);
        const voiceLevel = voiceEnergy / (voiceEndBin - voiceStartBin + 1);

        const now = Date.now();
        if (now - calibrationStartTime < calibrationDuration) {
          calibrationSamples++;
          backgroundNoiseLevelRef.current =
            (backgroundNoiseLevelRef.current * (calibrationSamples - 1) +
              voiceLevel) /
            calibrationSamples;
          adaptiveThresholdRef.current = Math.max(
            backgroundNoiseLevelRef.current * 2.5,
            3.0
          );
        }

        const peak = Math.max(
          ...Array.from(timeData).map((v) => Math.abs(v - 128))
        );
        const peakLevel = Math.min((peak / 128) * 100, 100);

        const voiceRatioThreshold = 0.2;
        const minVoiceLevel = Math.max(adaptiveThresholdRef.current * 0.7, 1.5);
        const minPeakLevel = 1.0;
        const minRMSLevel = 0.5;

        const hasVoice =
          voiceRatio > voiceRatioThreshold &&
          voiceLevel > minVoiceLevel &&
          peakLevel > minPeakLevel &&
          rms > minRMSLevel;

        voiceActivityBufferRef.current.push(hasVoice);
        if (voiceActivityBufferRef.current.length > 10) {
          voiceActivityBufferRef.current.shift();
        }

        const recentVoiceCount = voiceActivityBufferRef.current.filter(
          (v) => v
        ).length;
        const stableVoiceDetected = recentVoiceCount >= 3;

        if (stableVoiceDetected) {
          isCurrentlySpeakingRef.current = true;
          silenceStartTimeRef.current = null;
          setIsListening(true);
        } else {
          if (isCurrentlySpeakingRef.current) {
            if (silenceStartTimeRef.current === null) {
              silenceStartTimeRef.current = Date.now();
            } else {
              const silenceDuration = Date.now() - silenceStartTimeRef.current;
              if (silenceDuration > 1500) {
                isCurrentlySpeakingRef.current = false;
                setIsListening(false);
              }
            }
          } else {
            setIsListening(false);
          }
        }

        requestAnimationFrame(updateLevel);
      };
      updateLevel();

      let mimeType = 'audio/webm;codecs=opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/webm';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = '';
        }
      }
      mimeTypeRef.current = mimeType;

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: mimeType,
        audioBitsPerSecond: 128000,
      });

      console.log('🎤 MediaRecorder created:', {
        mimeType: mediaRecorder.mimeType,
        audioBitsPerSecond: 128000,
        state: mediaRecorder.state,
      });

      chunksRef.current = [];
      lastSendTimeRef.current = Date.now();
      isStoppingForSegmentRef.current = false;
      isStoppingRef.current = false;
      hasSentAudioRef.current = false;
      if (stopTimeoutRef.current) {
        clearTimeout(stopTimeoutRef.current);
        stopTimeoutRef.current = null;
      }

      mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
          const totalSize = chunksRef.current.reduce(
            (sum, c) => sum + c.size,
            0
          );
          console.log('🎤 Audio chunk received:', {
            size: event.data.size,
            sizeKB: (event.data.size / 1024).toFixed(2) + ' KB',
            type: event.data.type,
            totalChunks: chunksRef.current.length,
            totalSizeKB: (totalSize / 1024).toFixed(2) + ' KB',
          });
        } else {
          console.log('🎤 ⚠️ Empty data in chunk');
        }
      };

      const sendAccumulatedAudio = (): void => {
        if (hasSentAudioRef.current) {
          console.log('🎤 ⚠️ Audio already sent, ignoring duplicate call');
          return;
        }

        console.log('🎤 sendAccumulatedAudio called', {
          chunksCount: chunksRef.current.length,
          isStopping: isStoppingRef.current,
          hasSent: hasSentAudioRef.current,
        });

        if (stopTimeoutRef.current) {
          clearTimeout(stopTimeoutRef.current);
          stopTimeoutRef.current = null;
        }

        if (chunksRef.current.length === 0) {
          console.log('🎤 ⚠️ No audio chunks to send');
          isStoppingRef.current = false;
          return;
        }

        const blob = new Blob(chunksRef.current, {
          type: mimeTypeRef.current || 'audio/webm',
        });

        console.log('🎤 Audio blob created:', {
          size: blob.size,
          sizeKB: (blob.size / 1024).toFixed(2) + ' KB',
          type: blob.type,
          chunks: chunksRef.current.length,
        });

        if (blob.size >= 2000) {
          hasSentAudioRef.current = true;
          convertToBase64(blob);
          lastSendTimeRef.current = Date.now();
          console.log('🎤 ✅ Audio sent for transcription:', {
            size: blob.size,
            sizeKB: (blob.size / 1024).toFixed(2) + ' KB',
          });
        } else {
          console.log('🎤 ⚠️ Audio too small (< 2KB), likely too short:', {
            size: blob.size,
            sizeKB: (blob.size / 1024).toFixed(2) + ' KB',
          });
          alert('Recording too short. Please speak for at least 2-3 seconds.');
        }

        chunksRef.current = [];
        isStoppingRef.current = false;
      };

      mediaRecorder.onstop = () => {
        console.log('🎤 MediaRecorder onstop event fired', {
          chunksCount: chunksRef.current.length,
          state: mediaRecorder.state,
          hasSent: hasSentAudioRef.current,
        });

        if (stopTimeoutRef.current) {
          clearTimeout(stopTimeoutRef.current);
          stopTimeoutRef.current = null;
        }

        isStoppingRef.current = false;

        setTimeout(() => {
          if (!hasSentAudioRef.current && chunksRef.current.length > 0) {
            console.log('🎤 onstop timeout: Sending accumulated audio');
            sendAccumulatedAudio();
          } else if (hasSentAudioRef.current) {
            console.log('🎤 onstop timeout: Audio already sent, skipping');
          } else {
            console.log('🎤 onstop timeout: No chunks to send');
          }
        }, 200);
      };

      try {
        mediaRecorder.start(1000);
        mediaRecorderRef.current = mediaRecorder;
        console.log('🎤 MediaRecorder started (collecting chunks every 1s)');
        console.log('🎤 Talk as long as you want - click Stop when done');
      } catch (error) {
        console.error('Error starting MediaRecorder:', error);
        throw error;
      }
    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Failed to access microphone. Please check permissions.');
      if (onMuteToggle) {
        onMuteToggle();
      }
    }
  };

  const stopRecording = (): void => {
    console.log('🎤 stopRecording() called');

    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === 'recording'
    ) {
      console.log('🎤 MediaRecorder is recording, stopping...');
      mediaRecorderRef.current.stop();
      console.log(
        '🎤 MediaRecorder.stop() called - will collect final chunks and trigger onstop'
      );

      setTimeout(() => {
        if (streamRef.current) {
          streamRef.current.getTracks().forEach((track) => {
            track.stop();
            console.log('🎤 Stopped audio track:', track.label);
          });
        }
      }, 1000);
    } else {
      console.log(
        '🎤 MediaRecorder state:',
        mediaRecorderRef.current?.state || 'null'
      );
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    }

    setTimeout(() => {
      if (
        audioContextRef.current &&
        audioContextRef.current.state !== 'closed'
      ) {
        audioContextRef.current.close();
        console.log('🎤 Closed AudioContext');
      }
    }, 1500);

    setAudioLevel(0);
    setIsListening(false);
  };

  const convertToBase64 = (blob: Blob): void => {
    console.log('🎤 convertToBase64() called:', {
      size: blob.size,
      sizeKB: (blob.size / 1024).toFixed(2) + ' KB',
      type: blob.type,
      hasOnAudioData: typeof onAudioData === 'function',
    });

    if (!onAudioData) {
      console.error('🎤 ❌ onAudioData callback not provided!');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      const base64data = result.split(',')[1];
      console.log('🎤 ✅ Audio converted to base64:', {
        base64Length: base64data.length,
        base64Preview: base64data.substring(0, 50) + '...',
      });
      console.log('🎤 Calling onAudioData callback now...');
      try {
        onAudioData(base64data);
        console.log('🎤 ✅ onAudioData callback executed successfully');
      } catch (error) {
        console.error('🎤 ❌ Error in onAudioData callback:', error);
      }
    };
    reader.onerror = (error) => {
      console.error('🎤 ❌ Error converting audio to base64:', error);
    };
    reader.readAsDataURL(blob);
  };

  return (
    <div className="bg-white rounded-xl p-5">
      <div className="flex justify-between items-center mb-4">
        <h3 className="m-0 text-slate-800 text-xl font-semibold">
          Voice Input
        </h3>
        <button
          className={`px-5 py-2.5 border-none rounded-lg text-sm font-semibold cursor-pointer transition-all duration-300 ${
            isRecording
              ? 'bg-red-500 text-white animate-pulse'
              : 'bg-slate-200 text-slate-700 hover:bg-slate-300'
          }`}
          onClick={onMuteToggle}
        >
          {isRecording ? '⏹ Stop Recording' : '🎤 Start Recording'}
        </button>
      </div>

      {isRecording && (
        <div className="mt-4">
          <div
            className={`h-2 rounded transition-all duration-100 ${
              isListening ? 'animate-pulse' : ''
            }`}
            style={{
              width: `${Math.min(audioLevel * 1.5, 100)}%`,
              background: isListening
                ? 'linear-gradient(90deg, #10b981, #059669)'
                : 'linear-gradient(90deg, #667eea, #764ba2)',
            }}
          />
          <p
            className={`mt-2 text-sm font-semibold ${
              isMuted
                ? 'text-red-500'
                : isListening
                ? 'text-emerald-500'
                : 'text-slate-500'
            }`}
          >
            {isMuted
              ? '🔇 Muted'
              : isListening
              ? '● Listening...'
              : '○ Ready to listen'}
          </p>
        </div>
      )}
    </div>
  );
};

export default VoiceRecorder;
